import React, { useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import Time24Picker from "../ui/Time24Picker";
import { updateDemo } from "../../api/demosApi";
import { buildRescheduleSummary } from "../demos/rescheduleSummary";
import { rescheduleDemo, RESCHEDULE_CANCELLED } from "../demos/rescheduleConflict";
import { buildNoCorrespondeConfirm, buildNoCorrespondeSummary } from "../demos/noCorrespondeSummary";
import { OUTCOMES, STATUS_COLOR, isPendingFollowUp } from "../demos/demoConstants";
import { capitalizeName } from "../../utils/displayName";
import OriginChip from "../demos/OriginChip";
import InviteChip from "../demos/InviteChip";

// Extraído de LeadDemoSection.jsx para romper el import circular con ConfigCallBlock.jsx
// (que necesita ActiveDemoCard/ResolvedDemoRow y a su vez lo importa LeadDemoSection).
// Sin cambios de comportamiento respecto al original.

// Origen legible de una demo (coherente con MisAgendas/calendario).
export function demoOrigin(d) {
  if (d.origin === "config") return "Configuración";
  if (d.cold_caller) return capitalizeName(d.cold_caller);
  if (d.origin_detail === "setter_ia") return "Setter IA (llamada)";
  if (d.origin_detail === "whatsapp") return `WhatsApp${d.modalidad ? ` (${d.modalidad})` : ""}`;
  if (d.origin === "flow") return `Flow${d.modalidad ? ` (${d.modalidad})` : ""}`;
  return "Inbound";
}

export function ActiveDemoCard({ demo, onApplied, onNoCorresponde, simulated, outcomes = OUTCOMES, scheduledLabel = null }) {
  const { t, i18n } = useTranslation();
  const [note, setNote] = useState(demo.outcome_note || "");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("10:00");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const apply = async (status) => {
    if (status === "no_corresponde") {
      if (!window.confirm(buildNoCorrespondeConfirm(demo, Date.now(), t, i18n.language))) return;
    }
    if (status === "cancelada") {
      if (!window.confirm("¿Cancelar esta llamada de configuración? Se liberará el hueco del calendario.")) return;
    }
    setErr(""); setBusy(true);
    try {
      let new_scheduled_at;
      if (status === "reagendada") {
        if (!date) { setErr(t("demos.pickDate")); setBusy(false); return; }
        const [hh, mm] = time.split(":");
        const dt = new Date(date); dt.setHours(Number(hh), Number(mm), 0, 0);
        new_scheduled_at = dt.toISOString();
      }
      if (simulated) { onApplied?.(); return; }
      const payload = { demo_id: demo.demo_id, status, outcome_note: note, new_scheduled_at };
      const resp = status === "reagendada"
        ? await rescheduleDemo(payload, { t, lang: i18n.language })
        : await updateDemo(payload);
      if (resp === RESCHEDULE_CANCELLED) return;
      onApplied?.();
      if (status === "reagendada") window.alert(buildRescheduleSummary(resp, t));
      if (status === "no_corresponde") {
        window.alert(buildNoCorrespondeSummary(resp, t));
        // El backend sella `interest` en c_contactos; propaga el valor al modal para
        // que el formulario no quede con el interés viejo (si el admin luego guarda,
        // reescribiría por encima del sello con el `interest` desactualizado).
        onNoCorresponde?.();
      }
    } catch (e) {
      setErr(e?.response?.data?.error || t("demos.saveError"));
    } finally { setBusy(false); }
  };

  const pending = isPendingFollowUp(demo, Date.now());
  return (
    <div className={`p-3 rounded-2xl border ${pending ? "border-red-300 bg-red-50/50" : "border-indigo-200 bg-indigo-50/40"}`}>
      {/* flex-wrap: en el móvil los chips (origen + invitación) no caben junto al
          título y empujaban la ficha entera hacia la derecha. */}
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 mb-2">
        <span className="text-xs font-black uppercase tracking-wider text-indigo-700">
          {pending ? t("demos.pendingResult") : (scheduledLabel || t("demos.scheduled"))}
        </span>
        {/* Chip = de dónde vino el lead (primer toque); el texto = quién/por dónde se agendó. */}
        <span className="flex flex-wrap items-center gap-1.5 min-w-0">
          <OriginChip origin={demo.lead_origin} />
          <InviteChip demo={demo} />
          <span className="text-xs text-gray-500">{demoOrigin(demo)}</span>
        </span>
      </div>
      <textarea
        value={note} onChange={(e) => setNote(e.target.value)}
        placeholder={t("demos.resultNotes")}
        className="w-full border rounded-lg p-2 text-sm mb-2" rows={3}
      />
      <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-2 mb-2">
        <DatePicker selected={date} onChange={setDate} locale="es" dateFormat="dd/MM/yyyy"
          placeholderText={t("demos.rescheduleDate")} className="border rounded-lg px-2 py-1.5 text-sm w-full" />
        <Time24Picker value={time} onChange={(e) => setTime(e.target.value)} />
      </div>
      {err && <p className="text-red-600 text-xs mb-2">{err}</p>}
      <div className="flex flex-wrap gap-2">
        {outcomes.map((o) => (
          <button key={o.k} type="button" disabled={busy} onClick={() => apply(o.k)}
            className="px-3 py-1.5 rounded-lg border text-sm font-bold disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ResolvedDemoRow({ demo }) {
  const when = demo.scheduled_at ? new Date(demo.scheduled_at).toLocaleDateString("es-ES", { day: "numeric", month: "short" }) : "—";
  return (
    <div className="flex items-start justify-between gap-3 p-2.5 rounded-xl border border-gray-100 bg-white">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLOR[demo.status] || "bg-gray-100"}`}>{demo.status}</span>
          <span className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500">{when} · {demoOrigin(demo)} <OriginChip origin={demo.lead_origin} /></span>
        </div>
        {demo.outcome_note && <p className="text-sm text-gray-600 mt-1 break-words">{demo.outcome_note}</p>}
      </div>
    </div>
  );
}
